// hooks/useCollectionPage.js
import { useState, useEffect, useCallback } from 'react';
import useCollections from './useCollections';
import { migrateOldCollection } from '../utils/migrateCollection';

// Inline utility functions to avoid import issues
const extractUniqueTags = (collection) => {
  const tags = new Set();
  collection.forEach(item => {
    if (item.tags && Array.isArray(item.tags)) {
      item.tags.forEach(tag => tags.add(tag));
    }
  });
  return Array.from(tags);
};

const filterCollection = (collection, searchTerm, selectedTags) => {
  return collection.filter(item => {
    // Search filter
    if (searchTerm && !item.title?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    // Tag filter
    if (selectedTags.length > 0) {
      const itemTags = item.tags || [];
      const hasSelectedTag = selectedTags.some(tag => itemTags.includes(tag));
      if (!hasSelectedTag) return false;
    }
    
    return true;
  });
};

const sortCollectionByDate = (collection) => {
  return [...collection].sort((a, b) => 
    new Date(b.savedAt || 0) - new Date(a.savedAt || 0)
  );
};

const isValidCollectionItem = (item) => {
  return item && item.title && item.id;
};

const prepareCollectionData = (rawCollection) => {
  const validItems = rawCollection.filter(isValidCollectionItem);
  return sortCollectionByDate(validItems);
};

/**
 * Custom hook for managing collection page state and operations
 */
export const useCollectionPage = () => {
  const {
    getCollection,
    removeFromCollection,
    isModalOpen,
    currentItem,
    saveSuccess,
    note,
    setNote,
    closeModal,
    handleSave,
    isStorageAvailable
  } = useCollections();

  // State management
  const [collection, setCollection] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load and refresh collection data
  const loadCollection = useCallback(() => {
    try {
      setLoading(true);
      const rawCollection = getCollection();
      const preparedCollection = prepareCollectionData(rawCollection);
      setCollection(preparedCollection);
    } catch (error) {
      console.error('Error loading collection:', error);
      setCollection([]);
    } finally {
      setLoading(false);
    }
  }, [getCollection]);

  // Run migration on mount, then load collection
  useEffect(() => {
    // Run migration first (safe to call multiple times - checks if already done)
    const migrationResult = migrateOldCollection();
    if (migrationResult.migrated) {
      // console.log(`Migrated ${migrationResult.count} items from old format`);
    }
    
    // Then load the collection
    loadCollection();
    
    // Refresh collection every 5 seconds to catch external changes
    const interval = setInterval(loadCollection, 5000);
    return () => clearInterval(interval);
  }, [loadCollection]);

  // Get filtered collection based on search and tags
  const getFilteredCollection = useCallback(() => {
    return filterCollection(collection, searchTerm, selectedTags);
  }, [collection, searchTerm, selectedTags]);

  // Get all available tags
  const getAvailableTags = useCallback(() => {
    return extractUniqueTags(collection);
  }, [collection]);

  // Handle removing item from collection
  const handleRemoveItem = useCallback((itemId) => {
    if (removeFromCollection(itemId)) {
      loadCollection(); // Refresh the collection display
      return true;
    }
    return false;
  }, [removeFromCollection, loadCollection]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedTags([]);
  }, []);

  // Toggle tag selection
  const toggleTag = useCallback((tag) => {
    setSelectedTags(prev => {
      if (prev.includes(tag)) {
        return prev.filter(t => t !== tag);
      } else {
        return [...prev, tag];
      }
    });
  }, []);

  return {
    // Collection data
    collection,
    filteredCollection: getFilteredCollection(),
    availableTags: getAvailableTags(),
    loading,
    
    // Search and filter state
    searchTerm,
    setSearchTerm,
    selectedTags,
    toggleTag,
    clearFilters,
    
    // Collection operations
    handleRemoveItem,
    loadCollection,
    
    // Modal state from useCollections hook
    isModalOpen,
    currentItem,
    saveSuccess,
    note,
    setNote,
    closeModal,
    handleSave,
    isStorageAvailable
  };
};